import { useState } from 'preact/hooks';
import type { ClientView, TitleId } from '@gazette/shared';
import { canShareFiles, downloadGazette, shareGazette } from '../../art/export.ts';
import { t } from '../../i18n/index.ts';
import { actions, host, isHost, playerById } from '../../net/client.ts';
import { ErrorNotice, GameHeader, LeaveButton } from '../components/Chrome.tsx';
import { Headline } from '../components/Clipping.tsx';
import { GazetteSheet } from '../components/GazetteSheet.tsx';
import { Icon } from '../components/Icon.tsx';
import { Portrait } from '../components/Portrait.tsx';
import type { PhaseOf } from '../hooks.ts';

export function Final({ current, phase }: { current: ClientView; phase: PhaseOf<'final'> }) {
  const { results } = phase;
  const [busy, setBusy] = useState(false);
  const shareable = canShareFiles();
  const hasGazette = (current.gazette?.articles.length ?? 0) > 0;
  const plumeArticles = results.plume.articleIds.map((id) => current.gazette?.articles.find((article) => article.id === id)).filter(Boolean);

  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    try {
      await task();
    } finally {
      setBusy(false);
    }
  };

  const rankLabel = (rank: number) => (rank === 1 ? t('final.rank.first') : t('final.rank', { n: rank }));

  return (
    <>
      <GameHeader />
      <section class="sheet sheet--full" data-occlude aria-labelledby="results-title">
        <h2 id="results-title" class="sheet__title">
          {t('gz.palmares')}
        </h2>
        {!hasGazette && <p class="sheet__intro">{t('final.empty')}</p>}

        <ol class="ranking">
          {results.ranking.map((entry) => {
            const player = playerById(entry.playerId);
            if (!player) return null;
            return (
              <li class={`ranking__row ranking__row--${Math.min(entry.rank, 4)}`}>
                <span class="ranking__rank">{rankLabel(entry.rank)}</span>
                <Portrait seed={player.portrait} width={entry.rank === 1 ? 64 : 44} />
                <span class="ranking__name">{player.name}</span>
                <span class="ranking__score">{t('final.points', { n: entry.score })}</span>
              </li>
            );
          })}
        </ol>

        {results.titles.length > 0 && (
          <ul class="titles">
            {results.titles.map((title) => (
              <li class={`title-card title-card--${title.id}`}>
                <p class="title-card__name">
                  {title.id === 'plume' && <Icon name="quill" size={20} />}
                  {t(`title.${title.id as TitleId}`)}
                </p>
                <p class="title-card__who">{title.playerIds.map((id) => playerById(id)?.name ?? '?').join(', ')}</p>
                <p class="title-card__help">{t(`title.${title.id}.help`)}</p>
                {title.id === 'plume' &&
                  plumeArticles.map((article) => (
                    <p class="title-card__headline">
                      <Headline kicker={article!.kicker} headline={article!.headline} />
                    </p>
                  ))}
              </li>
            ))}
          </ul>
        )}
        {results.corbeau && !results.titles.some((title) => title.id.startsWith('corbeau')) && (
          <p class="sheet__intro">{t('final.corbeau', { name: playerById(results.corbeau.playerId)?.name ?? '?' })}</p>
        )}

        <div class="final-actions">
          {hasGazette && (
            <button type="button" class="btn btn--primary" disabled={busy} onClick={() => void run(() => downloadGazette(current))}>
              <Icon name="download" size={20} />
              {t('final.download')}
            </button>
          )}
          {hasGazette && shareable && (
            <button type="button" class="btn btn--secondary" disabled={busy} onClick={() => void run(() => shareGazette(current))}>
              <Icon name="share" size={20} />
              {t('final.share')}
            </button>
          )}
          {isHost.value ? (
            <button type="button" class="btn btn--secondary" onClick={() => void actions.restart()}>
              <Icon name="reroll" size={20} />
              {t('final.restart')}
            </button>
          ) : (
            <p class="hint">{t('final.waitHost', { name: host.value?.name ?? '' })}</p>
          )}
          <LeaveButton class="btn btn--link" label={t('final.leave')} confirm={t('final.leave.confirm')} showLabel />
        </div>
        <ErrorNotice />
        {hasGazette && <GazetteSheet current={current} revealed />}
      </section>
    </>
  );
}
